import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import MainLayout from '../layouts/MainLayout.vue'

// 扩展 RouteMeta 类型
declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    icon?: string
    requiresAuth?: boolean
    isAdmin?: boolean
    requiresDeviceTypeMaintenance?: boolean
    parent?: string
    hideInMenu?: boolean
    permission?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/official',
    name: 'official-website',
    component: () => import('../views/OfficialWebsite.vue'),
    meta: {
      title: '杭州钛瑞创泽科技有限公司',
      requiresAuth: false,
    },
  },
  {
    path: '/home',
    redirect: '/official',
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: {
      title: 'router.title.login',
      requiresAuth: false,
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/RegisterView.vue'),
    meta: {
      title: 'router.title.register',
      requiresAuth: false,
    },
  },
  {
    path: '/legal/terms',
    name: 'legal-terms',
    component: () => import('../views/legal/LegalPolicyView.vue'),
    meta: {
      title: 'AFSA 用户协议',
      requiresAuth: false,
    },
  },
  {
    path: '/legal/privacy',
    name: 'legal-privacy',
    component: () => import('../views/legal/LegalPolicyView.vue'),
    meta: {
      title: 'AFSA 隐私政策',
      requiresAuth: false,
    },
  },
  {
    path: '/forget',
    name: 'forget',
    component: () => import('../views/ForgetView.vue'),
    meta: {
      title: 'router.title.forget',
      requiresAuth: false,
    },
  },

  // 编排器 (Orchestrator) - 全屏独立布局
  {
    path: '/orchestrator',
    name: 'orchestrator',
    component: () => import('../views/orchestrator/OrchestratorFlowView.vue'),
    meta: {
      title: 'router.title.orchestrator',
      requiresAuth: false,
    },
  },

  // 小程序 WebView 使用的移动端独立页面
  {
    path: '/mobile/robot-call',
    name: 'mobile-robot-call',
    component: () => import('../views/mobile/RobotCallView.vue'),
    meta: {
      title: '小嘿视频通话',
      requiresAuth: false,
    },
  },

  // 主应用路由
  {
    path: '/',
    component: MainLayout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('../views/DashboardView.vue'),
        meta: {
          title: 'router.title.dashboard',
          icon: 'DashboardOutlined',
          requiresAuth: true,
        },
      },
      // 1. 机器人管理 (Robot Management) -> 现在叫 智能服务
      // 机器人管理 (Robot Management - New Submenu)
      {
        path: 'robot/list',
        name: 'robot-list',
        component: () => import('../views/robot/RobotListView.vue'),
        meta: {
          title: 'router.title.robotList',
          icon: 'RobotOutlined',
          parent: 'router.parent.robotManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'robot/detail/:id',
        name: 'robot-detail',
        component: () => import('../views/robot/RobotDetailView.vue'),
        meta: {
          title: 'router.title.robotDetail',
          parent: 'router.parent.robotManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'robot/console',
        name: 'robot-console',
        component: () => import('../views/robot/RobotConsoleView.vue'),
        meta: {
          title: 'router.title.robotConsole',
          icon: 'ControlOutlined',
          parent: 'router.parent.robotManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 声纹管理 (Voiceprint Management) - 已融入身份识别，菜单隐藏
      {
        path: 'robot/voiceprint',
        name: 'voiceprint-management',
        component: () => import('../views/robot/VoiceprintManagementView.vue'),
        meta: {
          title: 'router.title.voiceprintManagement',
          icon: 'AudioOutlined',
          parent: 'router.parent.robotManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // Robot Agent配置 (Robot Agent Config)
      {
        path: 'robot/agent-config',
        name: 'robot-agent-config',
        component: () => import('../views/robot/RobotAgentConfigView.vue'),
        meta: {
          title: 'router.title.robotAgentConfig',
          parent: 'router.parent.robotManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },

      // 知识图谱 (Knowledge Graph)

      // 1. 知识库管理 (Knowledge Base Management)
      {
        path: 'knowledge/bases',
        name: 'knowledge-bases',
        component: () => import('../views/knowledge/KnowledgeBaseListView.vue'),
        meta: {
          title: 'router.title.knowledgeBase',
          icon: 'DatabaseOutlined',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
        },
      },

      // 2. 知识点列表 (Knowledge Point List)
      {
        path: 'knowledge/points',
        name: 'knowledge-points',
        component: () => import('../views/knowledge/KnowledgePointListView.vue'),
        meta: {
          title: 'router.title.knowledgePoints',
          icon: 'UnorderedListOutlined',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
        },
      },
      // 2. 逻辑图谱 (Logic Graph)
      {
        path: 'knowledge/logic-graph',
        name: 'logic-graph',
        component: () => import('../views/knowledge/KnowledgeGraphListView.vue'),
        meta: {
          title: 'router.title.logicGraph',
          icon: 'DeploymentUnitOutlined',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'knowledge/logic-graph/design/:id',
        name: 'logic-graph-design',
        component: () => import('../views/knowledge/GraphManagementView.vue'),
        meta: {
          title: 'router.title.logicGraphDesign',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 图谱可视化 (Logic Graph Visualization)
      {
        path: 'knowledge/logic-graph/view/:id',
        name: 'logic-graph-view',
        component: () => import('../views/knowledge/LogicGraphView.vue'),
        meta: {
          title: 'router.title.logicGraphView',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 3. 文档库 (Document Library)
      {
        path: 'knowledge/documents',
        name: 'document-library',
        component: () => import('../views/knowledge/DocumentLibraryView.vue'),
        meta: {
          title: 'router.title.documentLibrary',
          icon: 'FileTextOutlined',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
        },
      },
      // 4. 文件库 (File Library)
      {
        path: 'knowledge/files',
        name: 'file-library',
        component: () => import('../views/knowledge/FileLibraryView.vue'),
        meta: {
          title: 'router.title.fileLibrary',
          icon: 'FolderOpenOutlined',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
        },
      },
      // 5. 知识抽取 (Knowledge Extraction) - 不在菜单显示
      {
        path: 'knowledge/extraction',
        name: 'knowledge-extraction',
        component: () => import('../views/knowledge/KnowledgeExtractionView.vue'),
        meta: {
          title: 'router.title.knowledgeExtraction',
          icon: 'BulbOutlined',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'knowledge/bases/:id',
        name: 'knowledge-bases-detail',
        component: () => import('../views/knowledge/KnowledgeBaseDetailView.vue'),
        meta: {
          title: 'router.title.knowledgeBaseDetail',
          parent: 'router.parent.knowledgeManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 流程管理 (Workflow Management)
      {
        path: 'workflow/list',
        name: 'workflow-list',
        component: () => import('../views/orchestrator/WorkflowListView.vue'),
        meta: {
          title: 'router.title.workflowManagement',
          icon: 'UnorderedListOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: false,
        },
      },

      // 角色管理 (Role Management)
      {
        path: 'agents',
        name: 'multimodal-agent-list',
        component: () => import('../views/agent/MultimodalAgentList.vue'),
        meta: {
          title: 'router.title.multimodalAgentList',
          icon: 'RobotOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'agents/create',
        name: 'multimodal-agent-create',
        component: () => import('../views/agent/MultimodalAgentForm.vue'),
        meta: {
          title: 'router.title.multimodalAgentCreate',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'agents/:id/edit',
        name: 'multimodal-agent-edit',
        component: () => import('../views/agent/MultimodalAgentForm.vue'),
        meta: {
          title: 'router.title.multimodalAgentEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },

      // Agent统一管理 (Unified Agent Management)
      {
        path: 'agents/unified',
        name: 'unified-agent-management',
        component: () => import('../views/agent/UnifiedAgentManagementView.vue'),
        meta: {
          title: 'router.title.unifiedAgentManagement',
          icon: 'AppstoreOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },

      // 模型管理 (Model Management) - 已废弃，整组隐藏
      {
        path: 'config/llm',
        name: 'config-llm',
        component: () => import('../views/config/LLMConfigView.vue'),
        meta: {
          title: 'router.title.llmConfig',
          parent: 'router.parent.modelManagement',
          requiresAuth: true,
          isAdmin: true,
          hideInMenu: true,
        },
      },
      {
        path: 'config/stt',
        name: 'config-stt',
        component: () => import('../views/config/SttConfigView.vue'),
        meta: {
          title: 'router.title.sttConfig',
          parent: 'router.parent.modelManagement',
          requiresAuth: true,
          isAdmin: true,
          hideInMenu: true,
        },
      },
      {
        path: 'config/tts',
        name: 'config-tts',
        component: () => import('../views/config/TtsConfigView.vue'),
        meta: {
          title: 'router.title.ttsConfig',
          parent: 'router.parent.modelManagement',
          requiresAuth: true,
          isAdmin: true,
          hideInMenu: true,
        },
      },
      // 智能体配置 (Agent Configuration) - 已废弃
      {
        path: 'config/agent',
        name: 'config-agent',
        component: () => import('../views/config/AgentView.vue'),
        meta: {
          title: 'router.title.agentConfig',
          parent: 'router.parent.modelManagement',
          requiresAuth: true,
          isAdmin: true,
          hideInMenu: true,
        },
      },
      {
        path: 'solution/template',
        name: 'template',
        component: () => import('../views/template/PromptTemplateManagement.vue'),
        meta: {
          title: 'router.title.template',
          icon: 'SnippetsOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          isAdmin: false,
          hideInMenu: false,
        },
      },
      // 魔法工具 (Magic Tools)
      {
        path: 'solution/magic-tools',
        name: 'magic-tools',
        component: () => import('../views/solution/MagicToolsView.vue'),
        meta: {
          title: 'router.title.magicTools',
          icon: 'ThunderboltOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
        },
      },
      // 工作流触发器 (Workflow Triggers) - 已废弃
      {
        path: 'workflow/triggers',
        name: 'workflow-triggers',
        component: () => import('../views/orchestrator/WorkflowTriggerView.vue'),
        meta: {
          title: 'router.title.workflowTriggers',
          icon: 'ClockCircleOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 数据管理 (Data Management)
      {
        path: 'multimodal/perception',
        name: 'multimodal-perception',
        component: () => import('../views/multimodal/PerceptionDataListView.vue'),
        meta: {
          title: 'router.title.perceptionData',
          icon: 'EyeOutlined',
          parent: 'router.parent.dataManagement',
          requiresAuth: true,
        },
      },
      // 多模态处理策略 (Multimodal Processing Strategy)
      {
        path: 'multimodal/strategy',
        name: 'processing-strategy',
        component: () => import('../views/multimodal/ProcessingStrategyListView.vue'),
        meta: {
          title: 'router.title.processingStrategy',
          icon: 'SettingOutlined',
          parent: 'router.parent.dataManagement',
          requiresAuth: true,
        },
      },
      // 记忆管理 (Memory Management)
      {
        path: 'memory/management',
        name: 'memory-management',
        component: () => import('../views/memory/MemoryManagementView.vue'),
        meta: {
          title: 'router.title.memoryManagement',
          icon: 'DatabaseOutlined',
          parent: 'router.parent.dataManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 记忆导入 (Memory Import)
      {
        path: 'memory/import',
        name: 'memory-import',
        component: () => import('../views/memory/MemoryImportView.vue'),
        meta: {
          title: 'router.title.memoryImport',
          icon: 'UploadOutlined',
          parent: 'router.parent.dataManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 设备管理 (Device Management)
      {
        path: 'device',
        redirect: 'device/list',
      },
      {
        path: 'device/list',
        alias: '/device/maintenance',
        name: 'device-list',
        component: () => import('../views/DeviceView.vue'),
        meta: {
          title: 'router.title.deviceMaintenance',
          icon: 'RobotOutlined',
          parent: 'router.parent.deviceManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'device/capability-maintenance',
        alias: '/device/capabilities',
        name: 'device-capability-maintenance',
        component: () => import('../views/device-capability/DeviceCapabilityMaintenanceView.vue'),
        meta: {
          title: 'router.title.deviceCapabilityMaintenance',
          icon: 'DeploymentUnitOutlined',
          parent: 'router.parent.deviceManagement',
          requiresAuth: true,
          requiresDeviceTypeMaintenance: true,
        },
      },
      {
        path: 'device/model-maintenance',
        alias: '/device/models',
        name: 'device-model-maintenance',
        component: () => import('../views/device-model/DeviceModelMaintenanceView.vue'),
        meta: {
          title: 'router.title.deviceModelMaintenance',
          icon: 'TagsOutlined',
          parent: 'router.parent.deviceManagement',
          requiresAuth: true,
          requiresDeviceTypeMaintenance: true,
        },
      },
      {
        path: 'device/detail/:deviceId',
        name: 'device-detail',
        component: () => import('../views/device/DeviceDetailView.vue'),
        meta: {
          title: 'router.title.deviceDetail',
          parent: 'router.parent.deviceManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'firmware/list',
        name: 'firmware-list',
        component: () => import('../views/firmware/FirmwareListView.vue'),
        meta: {
          title: 'router.title.firmwareManagement',
          icon: 'CloudUploadOutlined',
          parent: 'router.parent.deviceManagement', // 这里也需要确保指向 deviceManagement
          requiresAuth: true,
        },
      },

      // 工具库管理 (Tool Library Management)
      {
        path: 'tools/library',
        name: 'tool-library',
        component: () => import('../views/tools/ToolLibrary.vue'),
        meta: {
          title: 'router.title.toolLibrary',
          icon: 'ToolOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
        },
      },
      // MCP工具管理 (MCP Tool Management) - 已隐藏
      {
        path: 'tools/mcp',
        name: 'mcp-tool-management',
        component: () => import('../views/tools/McpToolManagement.vue'),
        meta: {
          title: 'router.title.mcpToolManagement',
          icon: 'ApiOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 工具配置页面 (Tool Configuration Pages)
      {
        path: 'tools/config/http',
        name: 'tool-config-http',
        component: () => import('../views/tools/config/HttpToolConfig.vue'),
        meta: {
          title: 'router.title.httpToolConfig',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/http/:id',
        name: 'tool-config-http-edit',
        component: () => import('../views/tools/config/HttpToolConfig.vue'),
        meta: {
          title: 'router.title.httpToolConfigEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/sql',
        name: 'tool-config-sql',
        component: () => import('../views/tools/config/SqlToolConfig.vue'),
        meta: {
          title: 'router.title.sqlToolConfig',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/sql/:id',
        name: 'tool-config-sql-edit',
        component: () => import('../views/tools/config/SqlToolConfig.vue'),
        meta: {
          title: 'router.title.sqlToolConfigEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/groovy',
        name: 'tool-config-groovy',
        component: () => import('../views/tools/config/GroovyToolConfig.vue'),
        meta: {
          title: 'router.title.groovyToolConfig',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/groovy/:id',
        name: 'tool-config-groovy-edit',
        component: () => import('../views/tools/config/GroovyToolConfig.vue'),
        meta: {
          title: 'router.title.groovyToolConfigEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/mqtt',
        name: 'tool-config-mqtt',
        component: () => import('../views/tools/config/MqttToolConfig.vue'),
        meta: {
          title: 'router.title.mqttToolConfig',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/mqtt/:id',
        name: 'tool-config-mqtt-edit',
        component: () => import('../views/tools/config/MqttToolConfig.vue'),
        meta: {
          title: 'router.title.mqttToolConfigEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/mcp',
        name: 'tool-config-mcp',
        component: () => import('../views/tools/config/McpToolConfig.vue'),
        meta: {
          title: 'router.title.mcpToolConfig',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/config/mcp/:id',
        name: 'tool-config-mcp-edit',
        component: () => import('../views/tools/config/McpToolConfig.vue'),
        meta: {
          title: 'router.title.mcpToolConfigEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 统一工具配置页面 (Unified Tool Config) - 新建
      {
        path: 'tools/unified/:type',
        name: 'unified-tool-config',
        component: () => import('../views/tools/UnifiedToolConfigView.vue'),
        meta: {
          title: 'router.title.unifiedToolConfig',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 统一工具配置页面 (Unified Tool Config) - 编辑
      {
        path: 'tools/unified/:type/:id',
        name: 'unified-tool-config-edit',
        component: () => import('../views/tools/UnifiedToolConfigView.vue'),
        meta: {
          title: 'router.title.unifiedToolConfigEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 工具调试路由
      {
        path: 'tools/debug',
        name: 'tool-debugger',
        component: () => import('../views/tools/debug/ToolDebugger.vue'),
        meta: {
          title: 'router.title.toolDebugger',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'tools/debug/:id',
        name: 'tool-debugger-with-id',
        component: () => import('../views/tools/debug/ToolDebugger.vue'),
        meta: {
          title: 'router.title.toolDebugger',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },

      // 4. 创意工坊 (Creative Workshop)
      // 4.1 魔法能力市场
      {
        path: 'workshop/magic-ability',
        name: 'magic-ability-market',
        component: () => import('../views/workshop/MagicAbilityMarketView.vue'),
        meta: {
          title: 'router.title.magicAbilityMarket',
          icon: 'ThunderboltOutlined',
          parent: 'router.parent.creativeWorkshop',
          requiresAuth: true,
        },
      },
      // 4.2 解决方案市场
      {
        path: 'workshop/solution',
        name: 'solution-market',
        component: () => import('../views/workshop/SolutionMarketView.vue'),
        meta: {
          title: 'router.title.solutionMarket',
          icon: 'SolutionOutlined',
          parent: 'router.parent.creativeWorkshop',
          requiresAuth: true,
        },
      },
      // 4.3 数据市场
      {
        path: 'workshop/data',
        name: 'data-market',
        component: () => import('../views/workshop/DataMarketView.vue'),
        meta: {
          title: 'router.title.dataMarket',
          icon: 'BarChartOutlined',
          parent: 'router.parent.creativeWorkshop',
          requiresAuth: true,
        },
      },
      // 4.4 设备市场
      {
        path: 'workshop/device',
        name: 'device-market-internal',
        component: () => import('../views/workshop/DeviceMarketInternalView.vue'),
        meta: {
          title: 'router.title.deviceMarketInternal',
          icon: 'ShopOutlined',
          parent: 'router.parent.creativeWorkshop',
          requiresAuth: true,
        },
      },
      {
        path: 'workshop',
        name: 'creative-workshop',
        component: () => import('../views/workshop/CreativeWorkshopView.vue'),
        meta: {
          title: 'router.title.creativeWorkshop',
          icon: 'BulbOutlined',
          parent: 'router.parent.creativeWorkshop',
          requiresAuth: true,
          hideInMenu: true, // Hide the overview page if we have sub-items
        },
      },

      // 新增. 监控运维 (Monitor & Ops)
      {
        path: 'monitor/dashboard',
        name: 'monitor-dashboard',
        component: () => import('../views/monitor/MonitorDashboardView.vue'),
        meta: {
          title: 'router.title.monitorDashboard',
          icon: 'DashboardOutlined',
          parent: 'router.parent.monitorOps',
          requiresAuth: true,
        },
      },
      {
        path: 'monitor/logs',
        name: 'monitor-logs',
        component: () => import('../views/monitor/MonitorLogsView.vue'),
        meta: {
          title: 'router.title.monitorLogs',
          icon: 'FileTextOutlined',
          parent: 'router.parent.monitorOps',
          requiresAuth: true,
        },
      },
      {
        path: 'monitor/audit',
        name: 'audit-logs',
        component: () => import('../views/monitor/AuditLogsView.vue'),
        meta: {
          title: 'router.title.auditLogs',
          icon: 'AuditOutlined',
          parent: 'router.parent.monitorOps',
          requiresAuth: true,
        },
      },

      // 新增. 数据中心 (Data Center)
      {
        path: 'data/analytics',
        name: 'data-analytics',
        component: () => import('../views/data/DataAnalyticsDashboardView.vue'),
        meta: {
          title: 'router.title.dataAnalytics',
          icon: 'BarChartOutlined',
          parent: 'router.parent.dataCenter',
          requiresAuth: true,
        },
      },
      {
        path: 'data/export',
        name: 'data-export',
        component: () => import('../views/data/DataExportView.vue'),
        meta: {
          title: 'router.title.dataExport',
          icon: 'CloudDownloadOutlined',
          parent: 'router.parent.dataCenter',
          requiresAuth: true,
        },
      },
      {
        path: 'data/import',
        name: 'data-import',
        component: () => import('../views/data/DataImportView.vue'),
        meta: {
          title: 'router.title.dataImport',
          icon: 'CloudUploadOutlined',
          parent: 'router.parent.dataCenter',
          requiresAuth: true,
        },
      },
      {
        path: 'data/change-log',
        name: 'data-change-log',
        component: () => import('../views/data/DataChangeLogView.vue'),
        meta: {
          title: 'router.title.dataChangeLog',
          icon: 'HistoryOutlined',
          parent: 'router.parent.dataCenter',
          requiresAuth: true,
        },
      },
      {
        path: 'data/api-audit',
        name: 'data-api-audit',
        component: () => import('../views/data/ApiAccessAuditView.vue'),
        meta: {
          title: 'router.title.apiAudit',
          icon: 'AuditOutlined',
          parent: 'router.parent.dataCenter',
          requiresAuth: true,
        },
      },

      // 新增. 系统安全 (System Security)
      {
        path: 'security/dashboard',
        name: 'security-dashboard',
        component: () => import('../views/security/SecurityDashboardView.vue'),
        meta: {
          title: 'router.title.securityDashboard',
          icon: 'SafetyCertificateOutlined',
          parent: 'router.parent.systemSecurity',
          requiresAuth: true,
        },
      },
      {
        path: 'security/sensitive-words',
        name: 'sensitive-words',
        component: () => import('../views/security/SensitiveWordsView.vue'),
        meta: {
          title: 'router.title.sensitiveWords',
          icon: 'StopOutlined',
          parent: 'router.parent.systemSecurity',
          requiresAuth: true,
        },
      },
      {
        path: 'security/refund-audit',
        name: 'refund-audit-queue',
        component: () => import('../views/security/RefundAuditQueue.vue'),
        meta: {
          title: '退款审核',
          icon: 'AuditOutlined',
          parent: 'router.parent.systemSecurity',
          requiresAuth: true,
          permission: 'risk:manage',
        },
      },
      // 风控规则配置
      {
        path: 'security/risk-rules',
        name: 'risk-rule-manage',
        component: () => import('../views/security/RiskRuleManage.vue'),
        meta: {
          title: '风控规则配置',
          icon: 'SafetyOutlined',
          parent: 'router.parent.systemSecurity',
          requiresAuth: true,
          permission: 'risk:manage',
        },
      },

      // 5. 系统设置 (System Settings)
      {
        path: 'setting/profile',
        name: 'setting-profile',
        component: () => import('../views/setting/ProfileView.vue'),
        meta: {
          title: 'router.title.personalCenter',
          icon: 'UserOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
        },
      },
      // 个人设置 (Personal Config)
      {
        path: 'setting/config',
        name: 'setting-config',
        component: () => import('../views/setting/ConfigView.vue'),
        meta: {
          title: 'router.title.personalConfig',
          icon: 'SettingOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
        },
      },
      {
        path: 'system/catalog',
        name: 'catalog-management',
        component: () => import('../views/catalog/CatalogManagement.vue'),
        meta: {
          title: 'Catalog 管理',
          icon: 'AppstoreOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
          isAdmin: true,
          permission: 'catalog:manage',
        },
      },
      {
        path: 'system/music-catalog',
        name: 'music-catalog-management',
        component: () => import('../views/music/MusicCatalogManagement.vue'),
        meta: {
          title: '音乐库管理',
          icon: 'CustomerServiceOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          isAdmin: true,
          permission: 'music:catalog:manage',
        },
      },
      {
        path: 'system/credential-center',
        name: 'credential-center',
        component: () => import('../views/system/CredentialCenterView.vue'),
        meta: {
          title: 'router.title.credentialCenter',
          icon: 'KeyOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
        },
      },
      {
        path: 'system/stellar-agent-prompt',
        name: 'stellar-agent-prompt',
        component: () => import('../views/system/StellarAgentPromptView.vue'),
        meta: {
          title: 'router.title.stellarAgentPrompt',
          icon: 'ControlOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
          isAdmin: true,
        },
      },
      {
        path: 'user',
        name: 'user',
        component: () => import('../views/UserView.vue'),
        meta: {
          title: 'router.title.user',
          icon: 'TeamOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
          isAdmin: true,
        },
      },
      {
        path: 'system/tenant',
        name: 'tenant',
        component: () => import('../views/system/TenantView.vue'),
        meta: {
          title: 'router.title.tenantManagement',
          icon: 'BankOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
        },
      },
      // 菜单管理 (Menu Management)
      {
        path: 'system/menu',
        name: 'menu-manage',
        component: () => import('../views/system/MenuManage.vue'),
        meta: {
          title: 'router.title.menuManagement',
          icon: 'MenuOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
          isAdmin: true,
        },
      },
      // 角色管理 (Role Management)
      {
        path: 'system/role',
        name: 'role-manage',
        component: () => import('../views/system/RoleView.vue'),
        meta: {
          title: 'router.title.roleManagement',
          icon: 'TeamOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
          isAdmin: true,
        },
      },

      // 权限管理 (Permission Management)
      {
        path: 'system/permission',
        name: 'permission-manage',
        component: () => import('../views/system/PermissionView.vue'),
        meta: {
          title: 'router.title.permissionManagement',
          icon: 'SafetyOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
          isAdmin: true,
        },
      },

      // Original non-grouped routes kept for fallback or specific direct access if needed, but mostly reorganized above.
      // Removing duplicates and old structure below.

      // Message View (Keeping it top level or under System? Maybe top level is fine or under Robot Management? User didn't specify Message Management.)
      // Let's put it under Robot Management -> Data Management for now, or keep top level.
      // User's list didn't include "Message". I'll hide it or put it under Data Management.
      // Let's put it under Robot Management -> Data Management (since messages are data).
      {
        path: 'message',
        name: 'message',
        component: () => import('../views/MessageView.vue'),
        meta: {
          title: 'router.title.message',
          icon: 'MessageOutlined',
          parent: 'router.parent.dataManagement',
          requiresAuth: true,
        },
      },

      // 测试对话页面
      {
        path: 'test-chat',
        name: 'test-chat',
        component: () => import('../views/TestChatView.vue'),
        meta: {
          title: 'router.title.testChat',
          icon: 'ExperimentOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: false,
          hideInMenu: false, // 生产环境也显示
        },
      },

      // WebRTC测试页面
      {
        path: 'webrtc-test',
        name: 'webrtc-test',
        component: () => import('../views/WebRTCTestView.vue'),
        meta: {
          title: 'WebRTC P2P测试',
          icon: 'VideoCameraOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: false,
          hideInMenu: false, // 生产环境也显示
        },
      },

      // SFU多人视频会议
      {
        path: 'meeting',
        name: 'sfu-meeting',
        component: () => import('../views/meeting/SfuMeetingView.vue'),
        meta: {
          title: '多人视频会议',
          icon: 'TeamOutlined',
          parent: 'router.parent.systemSettings',
          requiresAuth: true,
          hideInMenu: false,
        },
      },

      // ==================== 泡芙产品管理 (Paofu Product Management) ====================
      // 聊天室管理
      {
        path: 'paofu/chatrooms',
        name: 'paofu-chatrooms',
        component: () => import('../views/chatroom/ChatroomListView.vue'),
        meta: {
          title: 'router.title.chatrooms',
          icon: 'MessageOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'paofu/chatrooms/:roomId',
        name: 'paofu-chatroom-detail',
        component: () => import('../views/chatroom/ChatroomDetailView.vue'),
        meta: {
          title: 'router.title.chatroomDetail',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 娱乐中心：角色扮演剧本管理
      {
        path: 'paofu/admin/game/scripts',
        name: 'paofu-game-scripts',
        component: () => import('../views/paofu/PaofuGameScriptView.vue'),
        meta: {
          title: 'router.title.paofuGameScripts',
          icon: 'PlayCircleOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 角色扮演剧本详情 (隐藏菜单)
      {
        path: 'paofu/admin/game/scripts/:id',
        name: 'paofu-game-script-detail',
        component: () => import('../views/paofu/PaofuGameScriptView.vue'),
        meta: {
          title: 'router.title.paofuGameScriptDetail',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 表情资源管理
      {
        path: 'paofu/admin/expression',
        name: 'paofu-expression',
        component: () => import('../views/paofu/PaofuExpressionView.vue'),
        meta: {
          title: 'router.title.paofuExpression',
          icon: 'SmileOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 虚拟形象管理
      {
        path: 'paofu/admin/avatars',
        name: 'paofu-avatars',
        component: () => import('../views/paofu/PaofuVirtualAvatarView.vue'),
        meta: {
          title: 'router.title.paofuAvatars',
          icon: 'UserOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 资源版本管理
      {
        path: 'paofu/resource-version/:resourceId',
        name: 'PaofuResourceVersion',
        component: () => import('../views/paofu/PaofuResourceVersionView.vue'),
        meta: {
          title: '资源版本管理',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          isAdmin: true,
          hideInMenu: true,
        },
      },
      // 泡芙配置管理
      {
        path: 'paofu/admin/config',
        name: 'paofu-config',
        component: () => import('../views/paofu/PaofuConfigView.vue'),
        meta: {
          title: 'router.title.paofuConfig',
          icon: 'SettingOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙情绪管理
      {
        path: 'paofu/admin/emotion',
        name: 'paofu-emotion',
        component: () => import('../views/paofu/PaofuEmotionView.vue'),
        meta: {
          title: 'router.title.paofuEmotion',
          icon: 'HeartOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙数据分析
      {
        path: 'paofu/admin/analytics/dashboard',
        name: 'paofu-analytics-dashboard',
        component: () => import('../views/paofu/PaofuAnalyticsDashboardView.vue'),
        meta: {
          title: 'router.title.paofuAnalyticsDashboard',
          icon: 'LineChartOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'paofu/admin/analytics/events',
        name: 'paofu-analytics-events',
        component: () => import('../views/paofu/PaofuAnalyticsEventsView.vue'),
        meta: {
          title: 'router.title.paofuAnalyticsEvents',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'paofu/admin/analytics/behavior',
        name: 'paofu-analytics-behavior',
        component: () => import('../views/paofu/PaofuAnalyticsBehaviorView.vue'),
        meta: {
          title: 'router.title.paofuAnalyticsBehavior',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 泡芙活动管理
      {
        path: 'paofu/admin/activities',
        name: 'paofu-activities',
        component: () => import('../views/paofu/PaofuActivityListView.vue'),
        meta: {
          title: 'router.title.paofuActivities',
          icon: 'CalendarOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'paofu/admin/activities/rankings',
        name: 'paofu-activity-rankings',
        component: () => import('../views/paofu/PaofuActivityRankingsView.vue'),
        meta: {
          title: 'router.title.paofuActivityRankings',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 泡芙故事管理
      {
        path: 'paofu/admin/content',
        name: 'paofu-content',
        component: () => import('../views/paofu/PaofuContentView.vue'),
        meta: {
          title: 'router.title.paofuContent',
          icon: 'FileTextOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙成长任务管理
      {
        path: 'paofu/admin/growth',
        name: 'paofu-growth',
        component: () => import('../views/paofu/PaofuGrowthView.vue'),
        meta: {
          title: 'router.title.paofuGrowth',
          icon: 'RiseOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙商城资产管理
      {
        path: 'paofu/admin/mall/assets',
        name: 'paofu-mall-assets',
        component: () => import('../views/paofu/PaofuMallAssetsView.vue'),
        meta: {
          title: 'router.title.paofuMallAssets',
          icon: 'ShoppingOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙订单管理
      {
        path: 'paofu/admin/orders',
        name: 'paofu-orders',
        component: () => import('../views/paofu/PaofuOrderManageView.vue'),
        meta: {
          title: 'router.title.paofuOrders',
          icon: 'OrderedListOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙商品管理
      {
        path: 'paofu/admin/products',
        name: 'paofu-products',
        component: () => import('../views/paofu/PaofuProductManageView.vue'),
        meta: {
          title: 'router.title.paofuProducts',
          icon: 'ShoppingOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙社交管理
      {
        path: 'paofu/admin/social/friends',
        name: 'paofu-social-friends',
        component: () => import('../views/paofu/PaofuSocialFriendsView.vue'),
        meta: {
          title: 'router.title.paofuSocialFriends',
          icon: 'TeamOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      {
        path: 'paofu/admin/social/invites',
        name: 'paofu-social-invites',
        component: () => import('../views/paofu/PaofuSocialInvitesView.vue'),
        meta: {
          title: 'router.title.paofuSocialInvites',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      {
        path: 'paofu/admin/social/shares',
        name: 'paofu-social-shares',
        component: () => import('../views/paofu/PaofuSocialSharesView.vue'),
        meta: {
          title: 'router.title.paofuSocialShares',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 泡芙反馈管理
      {
        path: 'paofu/admin/feedback',
        name: 'paofu-feedback',
        component: () => import('../views/paofu/PaofuFeedbackManageView.vue'),
        meta: {
          title: 'router.title.paofuFeedback',
          icon: 'CommentOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 泡芙用户画像管理
      {
        path: 'paofu/admin/user-profiles',
        name: 'paofu-user-profiles',
        component: () => import('../views/paofu/PaofuUserProfilesView.vue'),
        meta: {
          title: 'router.title.paofuUserProfiles',
          icon: 'UserOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },

      // ==================== 客服中心 (Service Center) ====================
      // 客服概览
      {
        path: 'service/dashboard',
        name: 'service-dashboard',
        component: () => import('../views/service/ServiceDashboard.vue'),
        meta: {
          title: 'router.title.serviceDashboard',
          icon: 'CustomerServiceOutlined',
          parent: 'router.parent.serviceCenter',
          requiresAuth: true,
          permission: 'ticket:view',
        },
      },
      // 工单管理
      {
        path: 'service/tickets',
        name: 'ticket-manage',
        component: () => import('../views/service/TicketManage.vue'),
        meta: {
          title: 'router.title.ticketManage',
          icon: 'FileTextOutlined',
          parent: 'router.parent.serviceCenter',
          requiresAuth: true,
          permission: 'ticket:manage',
        },
      },
      // FAQ管理
      {
        path: 'service/faq',
        name: 'faq-manage',
        component: () => import('../views/service/FaqManage.vue'),
        meta: {
          title: 'router.title.faqManage',
          icon: 'QuestionCircleOutlined',
          parent: 'router.parent.serviceCenter',
          requiresAuth: true,
          permission: 'ticket:manage',
        },
      },
      // 使用手册管理
      {
        path: 'service/manual',
        name: 'support-manual-manage',
        component: () => import('../views/service/SupportManualManage.vue'),
        meta: {
          title: 'router.title.supportManualManage',
          icon: 'BookOutlined',
          parent: 'router.parent.serviceCenter',
          requiresAuth: true,
          permission: 'ticket:manage',
        },
      },

      // ==================== 商城中心 (Mall Center) ====================
      // 商品管理
      {
        path: 'mall/products',
        name: 'mall-products',
        component: () => import('../views/mall/ProductManage.vue'),
        meta: {
          title: 'router.title.mallProducts',
          icon: 'ShoppingOutlined',
          parent: 'router.parent.mallCenter',
          requiresAuth: true,
        },
      },
      // 订单管理
      {
        path: 'mall/orders',
        name: 'mall-orders',
        component: () => import('../views/mall/OrderManage.vue'),
        meta: {
          title: 'router.title.mallOrders',
          icon: 'OrderedListOutlined',
          parent: 'router.parent.mallCenter',
          requiresAuth: true,
        },
      },
      // 优惠券管理
      {
        path: 'mall/coupons',
        name: 'mall-coupons',
        component: () => import('../views/mall/CouponManage.vue'),
        meta: {
          title: 'router.title.mallCoupons',
          icon: 'GiftOutlined',
          parent: 'router.parent.mallCenter',
          requiresAuth: true,
        },
      },
      // 支付交易管理
      {
        path: 'mall/payments',
        name: 'mall-payments',
        component: () => import('../views/mall/PaymentTransactionManage.vue'),
        meta: {
          title: 'router.title.mallPayments',
          icon: 'TransactionOutlined',
          parent: 'router.parent.mallCenter',
          requiresAuth: true,
          permission: 'mall:payment:view',
        },
      },

      // ==================== 营销中心 (Marketing Center) ====================
      // 渠道管理
      {
        path: 'marketing/channels',
        name: 'channel-manage',
        component: () => import('../views/marketing/ChannelManage.vue'),
        meta: {
          title: 'router.title.channelManage',
          icon: 'FundOutlined',
          parent: 'router.parent.marketingCenter',
          requiresAuth: true,
          permission: 'channel:manage',
        },
      },
      // 渠道分析
      {
        path: 'marketing/channel-analytics',
        name: 'channel-analytics',
        component: () => import('../views/marketing/ChannelAnalytics.vue'),
        meta: {
          title: 'router.title.channelAnalytics',
          icon: 'LineChartOutlined',
          parent: 'router.parent.marketingCenter',
          requiresAuth: true,
          permission: 'channel:view',
        },
      },
      // 推送效果分析
      {
        path: 'marketing/push-analytics',
        name: 'push-analytics',
        component: () => import('../views/marketing/PushAnalytics.vue'),
        meta: {
          title: '推送效果分析',
          icon: 'NotificationOutlined',
          parent: 'router.parent.marketingCenter',
          requiresAuth: true,
        },
      },
      // 营销日历
      {
        path: 'marketing/calendar',
        name: 'marketing-calendar',
        component: () => import('../views/marketing/MarketingCalendar.vue'),
        meta: {
          title: '营销日历',
          icon: 'CalendarOutlined',
          parent: 'router.parent.marketingCenter',
          requiresAuth: true,
        },
      },

      // ==================== 财务中心 (Finance Center) ====================
      // 财务看板
      {
        path: 'finance/dashboard',
        name: 'finance-dashboard',
        component: () => import('../views/finance/FinanceDashboard.vue'),
        meta: {
          title: 'router.title.financeDashboard',
          icon: 'MoneyCollectOutlined',
          parent: 'router.parent.financeCenter',
          requiresAuth: true,
          permission: 'finance:view',
        },
      },
      {
        path: 'account-center',
        name: 'account-center-admin',
        redirect: '/account-center/overview',
        meta: {
          title: 'router.title.accountCenterAdmin',
          icon: 'IdcardOutlined',
          parent: 'router.parent.accountCenter',
          requiresAuth: true,
          permission: 'finance:view',
          hideInMenu: true,
        },
      },
      {
        path: 'account-center/overview',
        name: 'account-center-overview',
        component: () => import('../views/account/AccountCenterAdmin.vue'),
        meta: {
          title: 'router.title.accountCenterOverview',
          icon: 'DashboardOutlined',
          parent: 'router.parent.accountCenter',
          requiresAuth: true,
          permission: 'finance:view',
        },
      },
      {
        path: 'account-center/users',
        name: 'account-center-users',
        component: () => import('../views/account/AccountCenterAdmin.vue'),
        meta: {
          title: 'router.title.accountCenterUsers',
          icon: 'TeamOutlined',
          parent: 'router.parent.accountCenter',
          requiresAuth: true,
          permission: 'finance:view',
        },
      },
      {
        path: 'account-center/wallet',
        name: 'account-center-wallet',
        component: () => import('../views/account/AccountCenterAdmin.vue'),
        meta: {
          title: 'router.title.accountCenterWallet',
          icon: 'WalletOutlined',
          parent: 'router.parent.accountCenter',
          requiresAuth: true,
          permission: 'finance:view',
        },
      },
      {
        path: 'account-center/distribution',
        name: 'account-center-distribution',
        component: () => import('../views/account/AccountCenterAdmin.vue'),
        meta: {
          title: 'router.title.accountCenterDistribution',
          icon: 'ApartmentOutlined',
          parent: 'router.parent.accountCenter',
          requiresAuth: true,
          permission: 'finance:view',
        },
      },
      {
        path: 'account-center/verification',
        name: 'account-center-verification',
        component: () => import('../views/account/AccountCenterAdmin.vue'),
        meta: {
          title: 'router.title.accountCenterVerification',
          icon: 'SafetyCertificateOutlined',
          parent: 'router.parent.accountCenter',
          requiresAuth: true,
          permission: 'finance:view',
        },
      },
      {
        path: 'account-center/policies',
        name: 'account-center-policies',
        component: () => import('../views/account/AccountPolicyAdmin.vue'),
        meta: {
          title: '统一账号协议管理',
          icon: 'FileProtectOutlined',
          parent: 'router.parent.accountCenter',
          requiresAuth: true,
          isAdmin: true,
          permission: 'finance:view',
        },
      },

      // ==================== 任务管理 -> 泡芙产品直属子菜单 ====================
      {
        path: 'task/manage',
        name: 'task-manage',
        component: () => import('../views/task/TaskManage.vue'),
        meta: {
          title: 'router.title.taskManage',
          icon: 'CheckCircleOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },

      // ==================== 养成管理 -> 泡芙产品直属子菜单 ====================
      {
        path: 'growth/manage',
        name: 'growth-manage',
        component: () => import('../views/growth/GrowthManage.vue'),
        meta: {
          title: 'router.title.growthManage',
          icon: 'HeartOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },

      // ==================== 内容中心 -> 泡芙产品直属子菜单（区别于泡芙内容管理） ====================
      {
        path: 'content/manage',
        name: 'content-manage',
        component: () => import('../views/content/ContentManage.vue'),
        meta: {
          title: 'router.title.contentCenterManage',
          icon: 'FileTextOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
        },
      },
      // 文章管理（独立页面，便于详细编辑）
      {
        path: 'content/article',
        name: 'content-article',
        component: () => import('../views/content/ArticleManage.vue'),
        meta: {
          title: 'router.title.contentArticle',
          icon: 'EditOutlined',
          parent: 'router.parent.paofuManagement',
          requiresAuth: true,
          hideInMenu: true, // 隐藏菜单，从内容管理页面跳转
        },
      },

      // ==================== 用户会员中心 (User & Member Center) -> 泡芙产品子分组 ====================
      // 用户管理
      {
        path: 'member/users',
        name: 'member-user-manage',
        component: () => import('../views/member/UserManage.vue'),
        meta: {
          title: 'router.title.userManage',
          icon: 'UserOutlined',
          parent: 'router.parent.memberCenter',
          requiresAuth: true,
        },
      },
      // 会员管理
      {
        path: 'member/members',
        name: 'member-manage',
        component: () => import('../views/member/MemberManage.vue'),
        meta: {
          title: 'router.title.memberManage',
          icon: 'CrownOutlined',
          parent: 'router.parent.memberCenter',
          requiresAuth: true,
        },
      },
      // 用户分群管理
      {
        path: 'segment/manage',
        name: 'segment-manage',
        component: () => import('../views/segment/UserSegmentView.vue'),
        meta: {
          title: 'router.title.segmentManage',
          icon: 'TeamOutlined',
          parent: 'router.parent.memberCenter',
          requiresAuth: true,
        },
      },
      // 会员套餐管理
      {
        path: 'member/plans',
        name: 'member-plan-manage',
        component: () => import('../views/member/MemberPlanManage.vue'),
        meta: {
          title: 'router.title.memberPlanManage',
          icon: 'CrownOutlined',
          parent: 'router.parent.memberCenter',
          requiresAuth: true,
          isAdmin: true,
        },
      },
      // 会员订阅列表
      {
        path: 'member/subscriptions',
        name: 'member-subscription-list',
        component: () => import('../views/member/MemberSubscriptionList.vue'),
        meta: {
          title: 'router.title.memberSubscriptionList',
          icon: 'IdcardOutlined',
          parent: 'router.parent.memberCenter',
          requiresAuth: true,
        },
      },
      // ==================== 音色市场 (Voice Market) → 归入智能服务-智能方案 ====================
      // 音色管理列表
      {
        path: 'voice/profiles',
        name: 'voice-profile-list',
        component: () => import('../views/voice/VoiceProfileList.vue'),
        meta: {
          title: 'router.title.voiceProfileList',
          icon: 'AudioOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
        },
      },
      // 创建音色
      {
        path: 'voice/profiles/create',
        name: 'voice-profile-create',
        component: () => import('../views/voice/VoiceProfileForm.vue'),
        meta: {
          title: 'router.title.voiceProfileCreate',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
      // 编辑音色
      {
        path: 'voice/profiles/:id/edit',
        name: 'voice-profile-edit',
        component: () => import('../views/voice/VoiceProfileForm.vue'),
        meta: {
          title: 'router.title.voiceProfileEdit',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },

      // ==================== 意图识别调试 (Intent Recognition) - 已废弃 ====================
      {
        path: 'intent/test',
        name: 'intent-test',
        component: () => import('../views/intent/IntentTestView.vue'),
        meta: {
          title: 'router.title.intentTest',
          icon: 'AimOutlined',
          parent: 'router.parent.solutionManagement',
          requiresAuth: true,
          hideInMenu: true,
        },
      },
    ],
  },

  // 异常页面
  {
    path: '/403',
    name: '403',
    component: () => import('../views/exception/403.vue'),
    meta: {
      title: 'router.title.error403',
      requiresAuth: false,
    },
  },
  {
    path: '/404',
    name: '404',
    component: () => import('../views/exception/404.vue'),
    meta: {
      title: 'router.title.error404',
      requiresAuth: false,
    },
  },

  // 捕获所有未匹配的路由
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

export default router
